import { fetchAthleteGoals } from "@/api/athleteGoalsApi";
import {
  activateTrainingPlanMaterialisation,
  confirmTrainingPlanDraft,
  fetchCurrentActiveTrainingPlan,
  fetchCurrentTrainingBlock,
  generateTrainingPlan,
  materialiseTrainingPlanConfirmation,
  replaceActiveTrainingPlan,
} from "@/api/trainingPlanApi";
import { QUICK_PLAN_DURATION_OPTIONS } from "@/lib/trainingGoalOptions";
import {
  previewPlanStart,
  saveTrainingIntakeDraft,
} from "@/api/trainingIntakeApi";
import {
  coachingDecisionBlocksPlanGeneration,
  coachingDecisionPrerequisiteMessage,
} from "@/lib/planGenerationPrerequisites";
import {
  PLAN_GENERATION_RESULT_KIND,
  interpretPlanGenerationResult,
} from "@/lib/interpretPlanGenerationResult";
import { notifyActivePlanChanged } from "@/lib/activePlanSync";

export const QUICK_PLAN_LIFECYCLE_STAGE = Object.freeze({
  GENERATION: "generation",
  CONFIRMATION: "confirmation",
  MATERIALISATION: "materialisation",
  REPLACEMENT: "replacement",
  ACTIVATION: "activation",
});

const LIFECYCLE_STAGE_HEADLINE = Object.freeze({
  [QUICK_PLAN_LIFECYCLE_STAGE.GENERATION]: "Could not generate replacement plan",
  [QUICK_PLAN_LIFECYCLE_STAGE.CONFIRMATION]: "Plan generated but confirmation failed",
  [QUICK_PLAN_LIFECYCLE_STAGE.MATERIALISATION]: "Plan confirmed but materialisation failed",
  [QUICK_PLAN_LIFECYCLE_STAGE.REPLACEMENT]: "Replacement failed",
  [QUICK_PLAN_LIFECYCLE_STAGE.ACTIVATION]: "Activation failed",
});

export class QuickPlanLifecycleError extends Error {
  constructor(stage, message, { cause } = {}) {
    super(message);
    this.name = "QuickPlanLifecycleError";
    this.lifecycleStage = stage;
    if (cause) this.cause = cause;
  }
}

export const quickPlanLifecycleErrorMessage = (err) => {
  if (!(err instanceof QuickPlanLifecycleError)) {
    return err?.goalError?.message || err?.message || "Could not update your training plan.";
  }
  const headline = LIFECYCLE_STAGE_HEADLINE[err.lifecycleStage] || "Could not update your training plan";
  return `${headline}: ${err.message}`;
};

const newClientRequestId = () => {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return `qpu-${globalThis.crypto.randomUUID()}`;
  }
  return `qpu-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const wrapLifecycleFailure = (stage, err) => {
  if (err instanceof QuickPlanLifecycleError) {
    throw err;
  }
  const message = err?.planError?.message || err?.message || LIFECYCLE_STAGE_HEADLINE[stage];
  throw new QuickPlanLifecycleError(stage, message, { cause: err });
};

export const completeQuickTrainingPlanLifecycle = async ({
  draftResponse,
  activePlanConflictChoice = null,
  confirmDraft = confirmTrainingPlanDraft,
  materialiseConfirmation = materialiseTrainingPlanConfirmation,
  activateMaterialisation = activateTrainingPlanMaterialisation,
  replaceActivePlan = replaceActiveTrainingPlan,
  loadCurrentBlock = fetchCurrentTrainingBlock,
} = {}) => {
  const draftRef = draftResponse?.draft_ref;
  const draft = draftResponse?.draft;
  if (!draftRef) {
    throw new QuickPlanLifecycleError(
      QUICK_PLAN_LIFECYCLE_STAGE.GENERATION,
      "Plan generation did not return a draft reference.",
    );
  }

  let confirmation;
  try {
    confirmation = await confirmDraft(draftRef, {
      clientRequestId: newClientRequestId(),
      expectedCandidateId: draft?.candidate_id,
    });
  } catch (err) {
    wrapLifecycleFailure(QUICK_PLAN_LIFECYCLE_STAGE.CONFIRMATION, err);
  }
  if (!confirmation?.confirmation_ref) {
    throw new QuickPlanLifecycleError(
      QUICK_PLAN_LIFECYCLE_STAGE.CONFIRMATION,
      "Confirmation did not return a reference.",
    );
  }

  let materialisation;
  try {
    materialisation = await materialiseConfirmation(confirmation.confirmation_ref, {
      clientRequestId: newClientRequestId(),
    });
  } catch (err) {
    wrapLifecycleFailure(QUICK_PLAN_LIFECYCLE_STAGE.MATERIALISATION, err);
  }
  if (!materialisation?.materialisation_ref) {
    throw new QuickPlanLifecycleError(
      QUICK_PLAN_LIFECYCLE_STAGE.MATERIALISATION,
      "Materialisation did not return a reference.",
    );
  }

  let currentBlock = null;
  try {
    currentBlock = await loadCurrentBlock();
  } catch (_err) {
    currentBlock = null;
  }

  const shouldReplace = Boolean(currentBlock?.block_id)
    && activePlanConflictChoice !== "FINISH_CURRENT_PLAN_FIRST";

  let activation;
  try {
    if (shouldReplace) {
      activation = await replaceActivePlan(
        materialisation.materialisation_ref,
        {
          clientRequestId: newClientRequestId(),
          expectedCurrentBlockId: currentBlock.block_id,
        },
      );
    } else {
      activation = await activateMaterialisation(
        materialisation.materialisation_ref,
        { clientRequestId: newClientRequestId() },
      );
    }
  } catch (err) {
    wrapLifecycleFailure(
      shouldReplace
        ? QUICK_PLAN_LIFECYCLE_STAGE.REPLACEMENT
        : QUICK_PLAN_LIFECYCLE_STAGE.ACTIVATION,
      err,
    );
  }

  notifyActivePlanChanged({ source: "quick-plan-lifecycle", replaced: shouldReplace });

  return {
    draft: draftResponse,
    confirmation,
    materialisation,
    activation,
    replaced: shouldReplace,
    completed: true,
  };
};

export const saveQuickPlanGenerationParams = async ({
  startPreference,
  customStartDate = "",
  activePlanConflictChoice = null,
  preferredLongSessionDay = "",
}) => {
  await saveTrainingIntakeDraft({
    availability: {
      start_preference: startPreference || null,
      custom_start_date: customStartDate || null,
      active_plan_conflict_choice: activePlanConflictChoice || null,
      preferred_long_session_day: preferredLongSessionDay || null,
    },
  });
};

export const generateQuickTrainingPlan = async ({
  horizonWeeks,
  startPreference,
  customStartDate = "",
  activePlanConflictChoice = null,
  preferredLongSessionDay = "",
  loadCoachingDecision,
}) => {
  if (!Number.isInteger(horizonWeeks) || horizonWeeks < 1) {
    throw new Error("Choose how many weeks your plan should cover.");
  }
  if (!startPreference) {
    throw new Error("Choose when your plan should start.");
  }
  if (startPreference === "CUSTOM_DATE" && !customStartDate) {
    throw new Error("Choose a custom start date.");
  }

  await saveQuickPlanGenerationParams({
    startPreference,
    customStartDate,
    activePlanConflictChoice,
    preferredLongSessionDay,
  });

  const preview = await previewPlanStart({
    startPreference,
    customStartDate: startPreference === "CUSTOM_DATE" ? customStartDate : null,
    activePlanConflictChoice,
  });
  if (preview?.active_plan_conflict && !activePlanConflictChoice) {
    const err = new Error(
      "You already have an active plan that overlaps this start date. "
      + "Choose whether to replace it or finish it first.",
    );
    err.planConflict = preview.active_plan_conflict;
    throw err;
  }

  const coachingDecision = typeof loadCoachingDecision === "function"
    ? await loadCoachingDecision()
    : null;
  if (coachingDecisionBlocksPlanGeneration(coachingDecision)) {
    throw new Error(coachingDecisionPrerequisiteMessage(coachingDecision));
  }

  let raw;
  try {
    raw = await generateTrainingPlan({
      horizonWeeks,
      clientRequestId: newClientRequestId(),
    });
  } catch (err) {
    wrapLifecycleFailure(QUICK_PLAN_LIFECYCLE_STAGE.GENERATION, err);
  }
  const outcome = interpretPlanGenerationResult(raw);
  if (outcome.ok && outcome.kind === PLAN_GENERATION_RESULT_KIND.DRAFT_READY) {
    return completeQuickTrainingPlanLifecycle({
      draftResponse: outcome.response,
      activePlanConflictChoice,
    });
  }
  throw new QuickPlanLifecycleError(
    QUICK_PLAN_LIFECYCLE_STAGE.GENERATION,
    outcome.message || "Could not generate your training plan.",
  );
};

export const resolveQuickPlanHorizonWeeks = (activePlan, { fallback = 4 } = {}) => {
  const raw = activePlan?.weeks?.length
    || activePlan?.block_length_weeks
    || activePlan?.horizon_weeks;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < 1) return fallback;
  if (QUICK_PLAN_DURATION_OPTIONS.includes(parsed)) return parsed;
  return QUICK_PLAN_DURATION_OPTIONS.reduce(
    (best, option) => (
      Math.abs(option - parsed) < Math.abs(best - parsed) ? option : best
    ),
  );
};

export const loadQuickPlanPrefill = async ({
  loadGoals = fetchAthleteGoals,
  loadIntake,
  loadActivePlan = fetchCurrentActiveTrainingPlan,
} = {}) => {
  const [goals, intake, activePlan] = await Promise.all([
    loadGoals(),
    typeof loadIntake === "function" ? loadIntake() : Promise.resolve(null),
    typeof loadActivePlan === "function" ? loadActivePlan() : Promise.resolve(null),
  ]);
  const availability = intake?.intake?.availability || {};
  return {
    startPreference: availability.start_preference || "START_TOMORROW",
    customStartDate: availability.custom_start_date || "",
    activePlanConflictChoice: availability.active_plan_conflict_choice || null,
    preferredLongSessionDay: availability.preferred_long_session_day || "",
    planHorizonWeeks: resolveQuickPlanHorizonWeeks(activePlan),
    primaryGoal: (goals?.items || []).find((item) => item.is_primary) || null,
  };
};
